import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FIREBASE_CONFIG } from '../../config/Config';
import { fetchOfficeProfile } from '../../services/OfficeDataRepository';

const appId = FIREBASE_CONFIG.projectId;

/**
 * @deprecated O escritório deve ser consumido como cliente bruto via `useOfficeData()`.
 */
const buildFullAddress = (raw) => {
  const street = String(raw?.addressStreet || '').trim();
  const number = String(raw?.addressNumber || '').trim();
  const neighborhood = String(raw?.addressNeighborhood || '').trim();
  const city = String(raw?.addressCity || '').trim();
  const state = String(raw?.addressState || '').trim();
  const zip = String(raw?.addressZip || '').trim();
  const complement = String(raw?.addressComplement || '').trim();

  const parts = [];
  const line1 = [street, number].filter(Boolean).join(', ');
  const line2 = [neighborhood, city, state].filter(Boolean).join(' - ');
  if (line1) parts.push(line1);
  if (complement) parts.push(complement);
  if (line2) parts.push(line2);
  if (zip) parts.push(`CEP ${zip}`);
  return parts.join(', ');
};

/**
 * @deprecated O escritório deve ser consumido como cliente bruto via `useOfficeData()`.
 */
const normalizeOfficeToOwnerShape = (raw, fallback) => {
  const fb = fallback || {};
  const source = raw || {};

  const physical = source?.qualification?.physical || {};
  const contact = source?.contact || {};
  const prof = source?.professional || {};
  const addr = source?.location?.address || {};
  const bank = source?.financial?.bankAccount || {};

  const fullAddress =
    buildFullAddress(source) ||
    buildFullAddress({
      addressStreet: addr.street,
      addressNumber: addr.number,
      addressNeighborhood: addr.neighborhood,
      addressCity: addr.city,
      addressState: addr.state,
      addressZip: addr.zipCode,
      addressComplement: addr.complement
    }) ||
    String(source.fullAddress || '').trim() ||
    String(source.address || '').trim() ||
    String(fb.fullAddress || '').trim() ||
    '';
  const address =
    String(source.address || '').trim() ||
    String(addr.street || '').trim() ||
    [source.addressStreet, source.addressNumber, source.addressNeighborhood, source.addressCity, source.addressState]
      .filter(Boolean)
      .join(' - ') ||
    fb.address ||
    '';

  return {
    ...fb,
    ...source,
    name: String(physical.name || source.name || fb.name || '').trim(),
    socialName: String(physical.socialName || source.socialName || fb.socialName || '').trim(),
    pronoun: String(physical.pronoun || source.pronoun || fb.pronoun || '').trim(),
    genderIdentity: String(physical.genderIdentity || source.genderIdentity || fb.genderIdentity || '').trim(),
    grammaticalGender: String(physical.grammaticalGender || source.grammaticalGender || source.gender || fb.grammaticalGender || '').trim(),
    birthDate: String(physical.birthDate || source.birthDate || fb.birthDate || '').trim(),
    nationality: String(physical.nationality || source.nationality || fb.nationality || '').trim(),
    naturalness: String(physical.naturalness || source.naturalness || fb.naturalness || '').trim(),
    civilStatus: String(physical.civilStatus || source.civilStatus || fb.civilStatus || '').trim(),
    maritalRegime: String(physical.maritalRegime || source.maritalRegime || fb.maritalRegime || '').trim(),
    spouseName: String(physical.spouseName || source.spouseName || fb.spouseName || '').trim(),
    spouseCPF: String(physical.spouseCPF || source.spouseCPF || fb.spouseCPF || '').trim(),
    profession: String(physical.profession || source.profession || fb.profession || '').trim(),
    hasProfessionalRegister: Boolean(physical.hasProfessionalRegister || source.hasProfessionalRegister || fb.hasProfessionalRegister || prof.oab || source.oab),
    registerType: String(physical.registerType || source.registerType || fb.registerType || (prof.oab || source.oab ? 'OAB' : '')).trim(),
    registerUf: String(physical.registerUf || source.registerUf || prof.oabState || source.oabState || fb.registerUf || '').trim(),
    professionalRegister: String(physical.professionalRegister || source.professionalRegister || prof.oab || source.oab || fb.professionalRegister || '').trim(),
    isPPE: Boolean(physical.isPPE || source.isPPE || fb.isPPE),
    ppeDescription: String(physical.ppeDescription || source.ppeDescription || fb.ppeDescription || '').trim(),
    oab: String(prof.oab || source.oab || fb.oab || '').trim(),
    oabState: String(prof.oabState || source.oabState || fb.oabState || '').trim(),
    cpf: String(physical.cpf || source.cpf || fb.cpf || '').trim(),
    rg: String(physical.rg || source.rg || fb.rg || '').trim(),
    rgIssuer: String(physical.orgaoEmissor || source.rgIssuer || source.orgaoEmissor || fb.rgIssuer || fb.orgaoEmissor || '').trim(),
    email: String(contact.email || source.email || fb.email || '').trim(),
    address,
    fullAddress,
    phone: String(contact.phonePrimary || source.phone || fb.phone || '').trim(),
    phoneSecondary: String(contact.phoneSecondary || source.phoneSecondary || fb.phoneSecondary || '').trim(),
    bankInfo: String(source.bankInfo || fb.bankInfo || '').trim(),
    pixKey: String(bank.pix || source.pixKey || fb.pixKey || '').trim(),
    pixBank: String(source.pixBank || fb.pixBank || '').trim(),
    bankName: String(bank.bank || source.bankName || fb.bankName || '').trim(),
    bankAgency: String(bank.agency || source.bankAgency || fb.bankAgency || '').trim(),
    bankAccount: String(bank.account || source.bankAccount || fb.bankAccount || '').trim(),
    bankAccountType: String(source.bankAccountType || fb.bankAccountType || '').trim(),
  };
};

export const useOfficeData = () => {
  const { data, isLoading: loading, error } = useQuery({
    queryKey: ['officeProfile'],
    queryFn: fetchOfficeProfile,
    staleTime: 60000,
  });

  return { data: data || null, loading, error: error || null };
};
